import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { isNullOrEmpty } from '@awp/core/utils';
import { SirenAction, SirenLink, Parameters } from '../model';
import { RESPONSE_LOGGER_TOKEN, ResponseLogger } from '../response-console.logger';

export const ROUTE_PARAM_REGEX = /(?:\{)([a-zA-Z0-9]+)(?:[\?]?)(?:[\}])/g;


@Injectable({
  providedIn: 'root'
})
export class SirenHttp {

  private _headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private _encoder = new HttpUrlEncodingCodec();

  constructor(protected httpClient: HttpClient, private injector: Injector) { }

  public get<T>(link: SirenLink | string, param?: Parameters): Observable<T> {
    if (link == null) {
      throw new Error('Link is not defined');
    }

    const url = (typeof link === 'string') ? link : link.href;
    const loggers = <ResponseLogger[]>(this.injector.get(RESPONSE_LOGGER_TOKEN) as [] || []);
    const parsedUrl = this.parseUrlRouteParams(url, param ? param.route : null);

    return this.httpClient.get<T>(parsedUrl, { headers: this._headers })
      .pipe(
        tap(response => {
          loggers.forEach(logger => {
            logger.log(parsedUrl, response);
          });
        })
      );
  }

  public getBlob(link: SirenLink | string, param?: Parameters): Observable<Blob> {
    if (link == null) {
      throw new Error('Link is not defined');
    }

    const url = (typeof link === 'string') ? link : link.href;
    const parsedUrl = this.parseUrlRouteParams(url, param ? param.route : null);

    return this.httpClient.get(parsedUrl, { headers: this._headers, responseType: 'blob' });
  }

  public post<TRequest, TResponse>(action: SirenAction | string, request: TRequest, param?: Parameters): Observable<TResponse> {
    let url: string;
    let method = 'POST';

    if (action == null) {
      throw new Error('Action is not defined');
    }

    if (typeof action === 'string') {
      url = action;
    } else {
      url = action.href;
      method = action.method != null ? action.method : method;
    }

    const loggers = <ResponseLogger[]>(this.injector.get(RESPONSE_LOGGER_TOKEN) as [] || []);
    const parsedUrl = this.parseUrlRouteParams(url, param ? param.route : null);

    return this.httpClient.request<TResponse>(method, parsedUrl, {
        body: request,
        params: param != null ? param.queryParams : null,
        headers: this._headers
      })
      .pipe(
        tap(response => {
          loggers.forEach(logger => {
            logger.log(parsedUrl, response);
          });
        })
      );
  }

  private parseUrlRouteParams(url: string, params: any): string {
    if (params == null) return url;

    const result = url.replace(ROUTE_PARAM_REGEX, (match, key) => {
      const value = params[key];
      const encodedValue = isNullOrEmpty(value) ? value : this._encoder.encodeValue(value);
      return encodedValue;
    });

    return result;
  }

}
